/**
 * Nav.jsx — Fixed Navigation Bar
 *
 * Renders a full-width nav pinned to the top of the viewport.
 * Contains three elements:
 *   Left   — Face logo circle (clicking it resets to the browse homepage)
 *   Center — SVG wordmark logo + "Viri's Clayworks" italic text
 *   Right  — Hamburger menu button → opens a full-screen nav drawer
 *
 * Props:
 *   onReset       {Function} — resets app to "browse" step
 *   onOpenShop    {Function} — opens the shop page
 *   onViewClasses {Function} — scrolls to classes section
 *   onOpenFAQ     {Function} — opens the FAQ page
 */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ViriLogo from './icons/ViriLogo';
import ViriLogoFace from './icons/ViriLogoFace';

export default function Nav({ onReset, onOpenShop, onViewClasses, onOpenFAQ }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  function handleLogoClick() {
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      onReset();
    }
  }

  // Close drawer on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  function navigate(fn) {
    setOpen(false);
    fn();
  }

  const linkStyle = {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: 'clamp(1.05rem, 1.8vw, 1.25rem)',
    color: '#1a1208',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '10px 0',
    letterSpacing: '0.01em',
    lineHeight: 1.5,
    textAlign: 'left',
    transition: 'opacity 0.15s',
  };

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: isAdmin ? '#FCF7F1' : '#F9F2E9',
          borderBottom: 'none',
          padding: '10px 16px',
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* ── Grain texture overlay (non-admin only) ───────────────────────── */}
        {!isAdmin && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              backgroundImage: "url('/white-paper-texture (2).jpg')",
              backgroundRepeat: 'repeat',
              mixBlendMode: 'multiply',
              opacity: 0.35,
              filter: 'grayscale(1) sepia(0.2)',
              zIndex: 0,
            }}
          />
        )}

        {/* ── Left: Face logo — click to return to homepage ──────────────── */}
        <div
          onClick={handleLogoClick}
          className="nav-logo-icon"
          style={{
            cursor: 'pointer',
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            background: '#E8DDD0',
            border: 'none',
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <ViriLogoFace size={28} />
        </div>

        {/* ── Center: SVG paths logo + brand wordmark ─────────────────────── */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            zIndex: 1,
          }}
        >
          <ViriLogo
            size={62}
            showWordmark={false}
            strokeColor="#1a1208"
            strokeWidth={6}
          />
          <div
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              color: '#1a1208',
              letterSpacing: '0.01em',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: '1.2rem', paddingLeft: '6%' }}>
              Viri&#8217;s
            </div>
            <div style={{ fontSize: '0.9rem' }}>Clayworks</div>
          </div>
        </div>

        {/* ── Right: Hamburger / Close button (hidden on admin pages) ──────── */}
        {isAdmin ? (
          <div style={{ width: 38 }} />
        ) : (
          <button
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((o) => !o)}
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              border: 'none',
              background: '#E8A020',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              position: 'relative',
              zIndex: 201,
              flexShrink: 0,
            }}
          >
            {open ? (
              /* × close icon */
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <line x1="1" y1="1" x2="13" y2="13" />
                <line x1="13" y1="1" x2="1" y2="13" />
              </svg>
            ) : (
              /* ≡ hamburger lines */
              <>
                <div style={{ width: 14, height: 1.5, background: '#fff' }} />
                <div style={{ width: 14, height: 1.5, background: '#fff' }} />
                <div style={{ width: 14, height: 1.5, background: '#fff' }} />
              </>
            )}
          </button>
        )}
      </nav>

      {/* ── Backdrop ─────────────────────────────────────────────────────────── */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 199,
          background: 'rgba(26, 18, 8, 0.18)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'all' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* ── Slide-in panel from right ─────────────────────────────────────── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(300px, 85vw)',
          zIndex: 200,
          background: '#F9F2E9',
          boxShadow: open ? '-8px 0 48px rgba(26, 18, 8, 0.14)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 90,
          paddingLeft: 40,
          paddingRight: 32,
          paddingBottom: 48,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <button
          style={linkStyle}
          onClick={() => navigate(onReset)}
          onMouseEnter={(e) => (e.target.style.opacity = 0.5)}
          onMouseLeave={(e) => (e.target.style.opacity = 1)}
        >
          Home
        </button>
        <button
          style={linkStyle}
          onClick={() => navigate(onOpenShop)}
          onMouseEnter={(e) => (e.target.style.opacity = 0.5)}
          onMouseLeave={(e) => (e.target.style.opacity = 1)}
        >
          Shop
        </button>
        <button
          style={linkStyle}
          onClick={() => navigate(onViewClasses)}
          onMouseEnter={(e) => (e.target.style.opacity = 0.5)}
          onMouseLeave={(e) => (e.target.style.opacity = 1)}
        >
          Classes
        </button>
        <button
          style={linkStyle}
          onClick={() => navigate(onOpenFAQ)}
          onMouseEnter={(e) => (e.target.style.opacity = 0.5)}
          onMouseLeave={(e) => (e.target.style.opacity = 1)}
        >
          FAQ
        </button>

        {/* Social link — pinned to bottom */}
        <a
          href="https://www.instagram.com/virisclayworks"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            marginTop: 'auto',
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.75rem',
            fontStyle: 'normal',
            letterSpacing: '0.18em',
            color: '#a89e94',
            textDecoration: 'none',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => (e.target.style.opacity = 0.5)}
          onMouseLeave={(e) => (e.target.style.opacity = 1)}
        >
          @VIRISCLAYWORKS
        </a>
      </div>
    </>
  );
}
